import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Zap, CreditCard, Database } from 'lucide-react';
import { AnimationBoardTarget } from './AnimationBoardTarget';
import { useTranslation } from 'react-i18next';

export const ConnectionsAnimation: React.FC = () => {
    const { t } = useTranslation('common');

    const sources = [
        { icon: ShoppingBag, label: t('channels.shopify', 'Shopify'), delay: 0 },
        { icon: Zap, label: t('channels.google', 'Google Ads'), delay: 0.4 },
        { icon: CreditCard, label: t('channels.creditCard', 'Credit Card'), delay: 0.8 }
    ];

    return (
        <div className="relative h-56 sm:h-72 w-full flex items-center bg-neutral-50 rounded-xl border border-neutral-200 px-4 sm:px-10 overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-accent-primary/5 rounded-full blur-[80px] pointer-events-none" />

            <div className="flex-1 flex items-center h-full z-20">
                {/* Sources Column */}
                <div className="flex flex-col gap-5 sm:gap-7 flex-1">
                    {sources.map((s, i) => (
                        <div key={i} className="flex items-center w-full relative">
                            {/* Source Block */}
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: s.delay }}
                                className="flex items-center gap-2 sm:gap-3 relative z-30"
                            >
                                <div className="w-9 sm:w-11 h-9 sm:h-11 bg-white rounded-lg sm:rounded-xl border border-neutral-200 flex items-center justify-center shadow-sm flex-shrink-0">
                                    <s.icon className="w-4 sm:w-5 h-4 sm:h-5 text-accent-primary" />
                                </div>
                                <div className="hidden sm:flex flex-col min-w-0">
                                    <span className="text-[10px] text-neutral-900 font-bold whitespace-nowrap overflow-hidden text-ellipsis">{s.label}</span>
                                    <span className="text-[8px] text-neutral-400 uppercase tracking-tighter font-semibold">
                                        {t('featureExplanation.liveSource', 'Live Source')}
                                    </span>
                                </div>
                            </motion.div>

                            {/* Flexible Connection Line - This fills ALL remaining space */}
                            <div className="flex-1 h-px ml-2 sm:ml-4 relative -z-10">
                                <div className="absolute inset-0 bg-neutral-200" />

                                {/* Connection point on board indicator */}
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-accent-primary/10 border border-accent-primary/20 blur-[1px]" />

                                {/* Flowing Particle */}
                                <motion.div
                                    animate={{ left: ['0%', '100%'], opacity: [0, 1, 0] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: s.delay, ease: "linear" }}
                                    className="absolute top-1/2 -translate-y-1/2 text-emerald-500 select-none pointer-events-none"
                                >
                                    <Database className="w-3 sm:w-4 h-3 sm:h-4 fill-current" />
                                </motion.div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Target Column */}
                <div className="pl-4 sm:pl-10 flex-shrink-0">
                    <AnimationBoardTarget type="connections" />
                </div>
            </div>
        </div>
    );
};
